import React, {useMemo} from "react"

import {ReactKeycloakProvider} from "@react-keycloak/web"

import {createKeycloak} from "@auth/keycloak"

import {localStorageItemName} from "src/constants/local-storage-item-name"

/**
 * The shape of the tokens @react-keycloak reports.
 */
export interface Tokens {
    token?: string
    idToken?: string
}

/**
 * The shape of the `onEvent` handler @react-keycloak expects.
 */
export type OnEvent = NonNullable<React.ComponentProps<typeof ReactKeycloakProvider>["onEvent"]>

/**
 * Keeps `localStorage` in step with the tokens keycloak-js holds.
 *
 * `onTokens` fires on every provider event, including `onAuthLogout` where the
 * tokens are undefined. Writing them blindly stored the string "undefined" and
 * produced `Bearer undefined` on the next call, so each one is either written or
 * cleared.
 *
 * The refresh token is deliberately not persisted: keycloak-js keeps it in
 * memory, where an XSS cannot read it and a browser restart drops it. What
 * survives a restart is the Keycloak SSO session, which is enough to get a new
 * one silently.
 *
 * @param {Tokens} tokens - The tokens @react-keycloak reports.
 */
const persistTokens = (tokens: Tokens): void => {
    if (tokens.token) {
        localStorage.setItem(localStorageItemName.accessToken, tokens.token)
    } else {
        localStorage.removeItem(localStorageItemName.accessToken)
    }

    if (tokens.idToken) {
        localStorage.setItem(localStorageItemName.idToken, tokens.idToken)
    } else {
        localStorage.removeItem(localStorageItemName.idToken)
    }
}

/**
 * Logs the events @react-keycloak would otherwise swallow.
 *
 * Its own handlers for `onAuthError` and `onInitError` do nothing at all unless
 * `onEvent` is supplied, which is what made the session problems so hard to
 * observe in the first place.
 *
 * @param {AuthClientEvent} event - Name of the Keycloak lifecycle event.
 * @param {AuthClientError} error - The error, when the event carries one.
 */
const logEvent: OnEvent = (event: Parameters<OnEvent>[0], error: Parameters<OnEvent>[1]) => {
    if (error) {
        console.error(`[keycloak] ${event}`, error)
    }
}

/**
 * Props for the {@link KeycloakProvider} component.
 *
 * @property {string} keycloakUrl - Base URL of the Keycloak server, from settings.json.
 * @property {React.ReactNode} children - The child components to be wrapped by the provider.
 */
export type KeycloakProviderProps = {
    keycloakUrl: string
    children: React.ReactNode
}

/**
 * The app's Keycloak provider, configured.
 *
 * Wraps `ReactKeycloakProvider` so the call site only has to supply the server URL.
 *
 * @param {KeycloakProviderProps} props - The server URL and the tree to wrap.
 * @returns {JSX.Element} The configured provider.
 */
export const KeycloakProvider = ({keycloakUrl, children}: KeycloakProviderProps): JSX.Element => {
    // A Keycloak instance can only be initialised once per page load.
    // Memoising it ensures the same instance is used across re-renders.
    const authClient = useMemo(() => createKeycloak(keycloakUrl), [keycloakUrl])

    return (
        <ReactKeycloakProvider
            authClient={authClient}
            onEvent={logEvent}
            onTokens={persistTokens}
            initOptions={{
                onLoad: "check-sso",
                pkceMethod: "S256",
                silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
                enableLogging: import.meta.env.DEV
            }}
        >
            {children}
        </ReactKeycloakProvider>
    )
}

export default KeycloakProvider
